import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { AiOutlineReload } from 'react-icons/ai';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';

const Projects = () => {
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { axiosInstance } = useAuth();

    const isCreateDialogOpen = location.pathname === '/plan/new';

    const handleOpenChange = (open) => {
        if (open) {
            navigate('/plan/new');
        } else {
            navigate('/plan');
        }
    };

    const fetchProjects = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/projects');
            setProjects(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error('Error fetching projects, please try again later');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
            >
                <Card>
                    <CardHeader className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between">
                        <CardTitle className="text-3xl font-bold">Planner</CardTitle>
                        <div className="flex space-x-4">
                            <Button variant="ghost" size="icon" onClick={() => fetchProjects()}>
                                <AiOutlineReload className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => navigate('/plan/new')}>
                                Add Project
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Key</TableHead>
                                        <TableHead>Organization</TableHead>
                                        <TableHead>Tasks</TableHead>
                                        <TableHead>Role</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {[...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(5)].map((_, i) => (
                                                <TableCell key={i}>
                                                    <Skeleton className="h-8 w-full" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : projects.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Key</TableHead>
                                        <TableHead>Organization</TableHead>
                                        <TableHead>Tasks</TableHead>
                                        <TableHead>Role</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {projects.map((project) => (
                                        <TableRow 
                                            key={project.id} 
                                            className="cursor-pointer" 
                                            onClick={() => navigate(`/${project.organizationSlug}/plan/${project.key}/${project.defaultView}`)}
                                        >
                                            <TableCell>{project.name}</TableCell>
                                            <TableCell>{project.key}</TableCell>
                                            <TableCell>{project.organizationName}</TableCell>
                                            <TableCell>{project.taskCount}</TableCell>
                                            <TableCell>{project.role || 'Member'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-lg font-semibold">No projects found</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
            <CreateProjectDialog 
                open={isCreateDialogOpen}
                onOpenChange={handleOpenChange}
                onProjectCreated={() => {
                    fetchProjects();
                    navigate('/plan');
                }}
            />
        </Layout>
    );
};

export default Projects; 